import { properties } from "@repoflow/common/schema-fields";
import { siteKey } from "@common/schema/site/key";
// The /Site/Locale/* documents. A page's `locale` names a code its site
// declares, so the two shapes are one definition rather than two patterns that
// can drift apart.
import SiteFieldsSchema from "@common/schema/site/fields";
import PageEditorSchema from "./editor";
// The pointer to what is served — see the header there for the three states.
import PagePublishedSchema from "./published";

const { createdAt,
  updatedAt } = properties

export {
  createdAt,
  updatedAt
}

// The partition: `${orgId}/${siteId}`. See @common/schema/site/key for why a
// page is not hashed on the site's own slug.
export { siteKey };

export const orgId = {
  type: "string"
};

export const siteId = {
  type: "string"
};
export const pageId = {
  label: "PageId",
  type: "string"
};

export const title = {
  label: "Title",
  type: "string",
  minLength: 1
};

// The title on a TRANSLATE, where empty is a value and not an omission.
//
// `PageModel.translate` reads `input.title || page.title` — an empty title
// means "copy the source's", which is the common case and the reason the field
// is optional at all. The `minLength: 1` above says the opposite, and a form is
// validated as a WHOLE object: the translate form holds `title: ""` from the
// moment it mounts, so the shared field made the entire form invalid and left
// its Translate button permanently disabled. Nothing caught it — the screen's
// suite validated `{ locale }` rather than the object the form actually holds,
// and there was no browser test of the screen at all.
//
// A field of its own rather than dropping `minLength` from the one above: a
// page's title is genuinely required everywhere a page is NAMED, and Create and
// Update both rely on that.
export const translateTitle = {
  label: "Title",
  type: "string",
};

export const status = {
  type: "string",
};

// Where the page lives. This is the field a URL is resolved against — see
// api/code/src/render/resolve — and it is now the only identifier a page has
// besides its `pageId`.
//
// There used to be a `slug` beside it, carried over from the archived project's
// row and descriptive in the same way `route` was before anything served a
// page. Nothing ever resolved a page by it, no screen did anything with it that
// `title` did not do better, and gen-2's own Page tab had dropped it: two
// fields that both look like "the name in the URL", one of which is not, is a
// question every author had to ask once and nobody could answer from the
// screen. `route` is the one that answers it.
//
// A `body` went the same way, and for the plainer reason: it was never in the
// archived row either — gen-2's page carried `pageid`, `route`, `pageConfig`
// and `elements`, and nothing else — and nothing here ever read it. A page is
// composed out of its `elements`; the composer in common/render never looks at
// this field, so a "Body" box wide enough to type a document into was a field
// whose contents could not appear on the rendered page. No migration is owed:
// datorm's serialize() and deserialize() both walk the SCHEMA rather than the
// row, so an attribute the schema no longer names is neither read onto the
// entity nor written back. Rows that predate this keep theirs — update() is an
// UpdateItem over changed schema fields and removes nothing — and it is inert.
//
// Not /Route from @repoflow/schema/utils: that pattern requires a leading
// slash, and every page written before this change has no route at all. The
// empty alternative below is what lets those rows through their first update.
export const route = {
  label: "Route",
  $ref: "/Page/Route",
  default: "",
};

// WHICH LANGUAGE THIS PAGE IS WRITTEN IN
//
// Beside `route` because it belongs to the same decision: the language of a
// page is part of the URL that names it. A site declares its languages
// (@common/schema/site/fields), a page says which one of them it is.
//
// EMPTY MEANS "the site's default", the same convention `route`, `config` and
// every property of /PageConfig/Override already use — and it is what makes
// this change free for the rows that predate it. A page with no locale on a
// site with no locales resolves through `localeOf` in @common/render/page to
// "en", which is byte-for-byte the value that was hard-coded into <html lang>
// before this field existed. There is nothing to backfill.
//
// It names a /Site/Locale/Code rather than restating the pattern, so a code
// that is storable on a page is exactly one that is declarable on a site.
export const locale = {
  label: "Locale",
  $ref: "/Site/Locale/Code",
  default: "",
};

// WHICH PAGES ARE THE SAME PAGE IN ANOTHER LANGUAGE
//
// A free-form name shared by every translation of one page — "pricing" on
// /pricing, /es/precios and /de/preise alike. It exists because nothing else
// can answer the question, and two things need it answered: `hreflang`, which
// is a statement that these URLs are one another's translations, and the
// editor, which otherwise shows a site's languages as unrelated rows.
//
// NOT DERIVED FROM THE ROUTE, and that is the whole point. Stripping a `/es/`
// prefix would group /es/pricing with /pricing and would work — right up until
// somebody translates the SLUG, which is most of the reason to do any of this:
// /es/precios is the URL a Spanish reader and a search engine both want. A
// grouping key is what buys localised slugs.
//
// EMPTY MEANS "not part of a group", and it is the default, so a page written
// before this field existed emits no alternates and no canonical rather than
// claiming to be the only translation of itself.
//
// NO SCREEN RENDERS IT, and the `label` is kept for the same reason the field
// is: it is written by `translate` and read by `alternatesOf`, the pages
// listing and the page screen's Translations table, none of which put the
// value itself in front of anybody. The key is minted from a `pageId` when
// nobody names one, so an editable box here showed an author an id and invited
// them to edit it — see the note where it was, in routes/.../pages/page/edit/
// page, for what an edit of it silently does.
export const translationKey = {
  label: "Translation group",
  $ref: "/Page/TranslationKey",
  default: "",
};

// The ordered content of the page. Order IS the array order — there is no
// position column and no server-side reorder mutation; the whole array is
// written on every update, which is what the archived project did and what the
// list widget's drag-and-drop produces.
//
// `index` names the property rows are keyed by. Without it the list widget
// renders but will not drag.
export const elements = {
  label: "Elements",
  type: "array",
  items: { $ref: "/Page/Element" },
  index: "elementid",
  default: [],
};

// This page's half of the document shell, merged over its site's.
//
// The site's `config` is the shell every page of the site is composed into;
// this is what THIS page changes about it. Every property means "leave the
// site's alone" when it holds its empty default, so a page that overrides
// nothing carries a full object of empties rather than a null — see
// /PageConfig/Override in @common/schema/page-config, and `mergeConfig` in
// @common/render/page for what the merge does with each kind of field.
//
// Two forms of one field, same split and same reason as a site's: `config` is
// what a form renders and what the API validates, `storedConfig` is what the
// table can validate, and the difference is the `resolver` union inside
// `types`.
export const config = {
  label: "Configuration",
  $ref: "/PageConfig/Override",
};

export const storedConfig = {
  $ref: "/Data/PageConfig/Override",
};

// How the page is composed while it is being edited. Not content — see
// ./editor for why it is stored on the page anyway.
//
// No `storedEditor` twin below: unlike `elements` and `types`, /Page/Editor
// carries no `resolver` union, so raw jsonschema validates it correctly and
// datorm can hold the real shape.
export const editor = {
  label: "Editor",
  $ref: "/Page/Editor",
  default: { params: [] },
};

// Which version of this page visitors get, and on which route. Stored on the
// row, read by the render path and by the API's `served`/`changed` answers,
// written by nothing but ../../../api/code/src/model/page/publish — no form
// carries it, so it is on /Data/Page and /Public/Page and on no input shape.
export const published = {
  $ref: "/Page/Published",
};

// Extra documents these fields reference. Spread by data.js and public.js the
// same way UtilsSchema is.
export default [
  ...PageEditorSchema,
  ...PagePublishedSchema,
  // `locale` is `$ref: /Site/Locale/Code`, so the document it names has to be
  // in the same schema list — the same way `config` pulls in PageConfigSchema.
  ...SiteFieldsSchema,
  {
    // A name an author types, not an id: it is shared across rows on purpose
    // and has to be legible in a list next to the pages it groups. Narrow
    // enough to stay a single token, so it can never be confused for a route.
    id: "/Page/TranslationKey",
    type: "string",
    maxLength: 64,
    pattern: "^$|^[a-zA-Z0-9_-]{1,64}$",
  },
  {
    id: "/Page/Route",
    type: "string",
    maxLength: 256,
    // `:` and `?` are what let a route be a PATTERN — `/blog/:slug`,
    // `/blog/:page?` — see ./route for the grammar the charset admits.
    pattern: "^$|^/[/.:?a-zA-Z0-9-]*$",
  },
];

// `elements` as it is STORED. Same reason as storedTypes in
// @common/schema/content/fields: /Page/Element.config is a `oneOf` selected by
// the custom `resolver` keyword, and datorm validates writes with raw
// jsonschema, which resolves that literally and rejects every real element.
// The API boundary checks it resolver-aware instead.
export const storedElements = {
  type: "array",
  items: { type: "object" },
  default: [],
};
